import RevealOnScroll from '../base/RevealOnScroll';

const benefits = [
  {
    icon: 'ri-time-line',
    title: 'On Time, Done Right',
    body: 'Every project is completed on schedule and held to the highest professional standard. No exceptions.',
  },
  {
    icon: 'ri-money-dollar-circle-line',
    title: 'Fair & Honest Pricing',
    body: 'Transparent quotes with no hidden fees. You know exactly what you\'re paying before we begin.',
  },
  {
    icon: 'ri-user-star-line',
    title: '16+ Years of Houston Expertise',
    body: 'Deep local knowledge of Houston homes, codes, and conditions built over 16+ years of active service.',
  },
  {
    icon: 'ri-loop-left-line',
    title: '60%+ Repeat & Referral Business',
    body: 'Most of our Houston business comes from clients who return and refer friends. Our reputation says it all.',
  },
  {
    icon: 'ri-verified-badge-line',
    title: 'Satisfaction Guarantee',
    body: 'We don\'t consider a job complete until you\'re 100% satisfied. Your investment is fully protected.',
  },
  {
    icon: 'ri-calendar-check-line',
    title: 'Available 7 Days a Week',
    body: 'Home repairs don\'t follow a 9-to-5 schedule. We\'re available every day across Greater Houston TX.',
  },
];

interface Props {
  image?: string;
  title?: string;
  subtitle?: string;
}

export default function WhyChooseUs({
  image = 'https://public.readdy.ai/ai/img_res/886dc924799a5e894ef598bf51008fc3.jpg',
  title = 'Why Houston Homeowners Choose One Call',
  subtitle = 'Built on 16+ years of earned trust, transparent pricing, and an unwavering commitment to doing every job right the first time.',
}: Props) {
  return (
    <section className="bg-white py-28 lg:py-36 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 xl:gap-24 items-start">

          {/* Left — image with floating stats */}
          <RevealOnScroll direction="left">
            <div className="relative">
              {/* Main image */}
              <div className="relative h-[580px] overflow-hidden">
                <img
                  src={image}
                  alt="One Call Handyman Service — Houston TX professionals"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              </div>

              {/* Floating stat card — bottom right */}
              <div className="absolute -bottom-6 -right-4 lg:-right-8 bg-[#BF9448] p-6 min-w-[160px]">
                <div className="font-serif text-white text-4xl font-bold leading-none">16+</div>
                <div className="font-sans text-white/80 text-xs uppercase tracking-widest mt-2">
                  Years in Houston
                </div>
              </div>

              {/* Floating accent card — top left */}
              <div className="absolute -top-5 -left-4 lg:-left-6 bg-[#0F0F0F] p-4">
                <div className="flex items-center gap-2 mb-1">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <span key={s} className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-star-fill text-[#BF9448] text-xs"></i>
                    </span>
                  ))}
                </div>
                <div className="font-sans text-white text-xs font-semibold">5.0 Google Rating</div>
                <div className="font-sans text-white/40 text-[10px] mt-0.5">10+ Verified Reviews</div>
              </div>
            </div>
          </RevealOnScroll>

          {/* Right — content */}
          <RevealOnScroll direction="right">
            <div className="pt-0 lg:pt-10">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-px bg-[#BF9448]" />
                <span className="text-[#BF9448] text-[10px] font-sans font-semibold tracking-[0.25em] uppercase">
                  Why Choose Us
                </span>
              </div>
              <h2
                className="font-serif font-semibold text-[#0F0F0F] leading-tight mb-4"
                style={{ fontSize: 'clamp(1.9rem, 3vw, 2.75rem)' }}
              >
                {title}
              </h2>
              <p className="font-sans text-[#7A7265] text-base leading-relaxed mb-12">
                {subtitle}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {benefits.map((b, i) => (
                  <div key={i} className="group flex gap-4">
                    <div className="flex-shrink-0 mt-0.5">
                      <span className="w-8 h-8 flex items-center justify-center border border-[#BF9448]/30 group-hover:border-[#BF9448] group-hover:bg-[#BF9448]/5 transition-all duration-200">
                        <i className={`${b.icon} text-[#BF9448] text-base`}></i>
                      </span>
                    </div>
                    <div>
                      <div className="font-sans text-[#0F0F0F] text-sm font-semibold mb-1">{b.title}</div>
                      <p className="font-sans text-[#8A8278] text-xs leading-relaxed">{b.body}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-4 mt-12 pt-10 border-t border-[#E8E2D9]">
                <a
                  href="#quote"
                  className="bg-[#0F0F0F] hover:bg-[#1A1A1A] text-white font-sans text-xs font-semibold px-8 py-4 rounded-sm transition-colors duration-200 whitespace-nowrap cursor-pointer tracking-[0.12em] uppercase"
                >
                  Get Free Quote
                </a>
                <a
                  href="tel:3463638363"
                  className="flex items-center gap-2 text-[#6B6358] hover:text-[#BF9448] font-sans text-sm transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-phone-fill text-[#BF9448] text-sm"></i>
                  </span>
                  346-363-8363
                </a>
              </div>
            </div>
          </RevealOnScroll>

        </div>
      </div>
    </section>
  );
}
